// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, xClassNames } from "@vanmeter/XDL/Utilities";
import React, { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";

interface ProductGridContainerContextType {
    styles: ProductGridContainerStyle;
}
export interface ProductGridContainerStyle {
    container?: InjectableCss;
    image?: InjectableCss;
    brand?: InjectableCss;
    title?: InjectableCss;
    description?: InjectableCss;
    partNumbers?: InjectableCss;
    availability?: InjectableCss;
    badges?: InjectableCss;
    price?: InjectableCss;
    addToCartActions?: InjectableCss;
    addToList?: InjectableCss;
}

export const productGridContainerStyle: ProductGridContainerStyle = {};

const ProductGridContainerContext = React.createContext<ProductGridContainerContextType>({
    styles: {},
});
const sharedClassName = "product-grid-container";

const enum NAMES {
    Image = "Image",
    Brand = "Brand",
    Title = "Title",
    Description = "Description",
    PartNumbers = "PartNumbers",
    Availability = "Availability",
    Badges = "Badges",
    Price = "Price",
    AddToCartActions = "AddToCartActions",
    AddToList = "AddToList",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Brand),
        getChild(childrenArray, NAMES.Title),
        getChild(childrenArray, NAMES.Description),
        getChild(childrenArray, NAMES.PartNumbers),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.Badges),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.AddToList),
    ];
};

const ProductGridContainer = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: any;
    extendedStyles?: ProductGridContainerStyle;
    [key: string]: any;
}) => {
    const [styles, setStyles] = useState(() => mergeWithCss(productGridContainerStyle, extendedStyles));

    useEffect(() => {
        setStyles(() => mergeWithCss(productGridContainerStyle, extendedStyles));
    }, [extendedStyles]);

    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [image, brand, title, description, partNumbers, availability, badges, price, addToCartActions, addToList] =
        React.useMemo(() => getDestructeredChildren(childrenArray), [childrenArray]);
    return (
        <ProductGridContainerContext.Provider value={value}>
            <StyledWrapper
                {...styles.container}
                {...otherProps}
                className={xClassNames.append(sharedClassName, otherProps.className)}
            >
                {/* 10 Sections */}
                {image}
                {brand}
                {title}
                {description}
                {partNumbers}
                {availability}
                {badges}
                {price}
                {addToCartActions}
                {addToList}
            </StyledWrapper>
        </ProductGridContainerContext.Provider>
    );
};

export const useProductGridContainerContext = () => {
    const context = React.useContext(ProductGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductGridContainer");
    }
    return context;
};

const Image: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Brand: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { brand },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...brand} className={`${sharedClassName}-brand`}>
            {children}
        </StyledWrapper>
    );
};

const Title: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { title },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...title} className={`${sharedClassName}-title`}>
            {children}
        </StyledWrapper>
    );
};
const Description: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { description },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...description} className={`${sharedClassName}-description`}>
            {children}
        </StyledWrapper>
    );
};

const PartNumbers: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { partNumbers },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...partNumbers} className={`${sharedClassName}-partNumbers`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};

const Badges: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { badges },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...badges} className={`${sharedClassName}-badges`}>
            {children}
        </StyledWrapper>
    );
};

const Price: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} className={`${sharedClassName}-addToList`}>
            {children}
        </StyledWrapper>
    );
};

ProductGridContainer.Image = Image;
ProductGridContainer.Image.displayName = NAMES.Image;

ProductGridContainer.Brand = Brand;
ProductGridContainer.Brand.displayName = NAMES.Brand;

ProductGridContainer.Title = Title;
ProductGridContainer.Title.displayName = NAMES.Title;

ProductGridContainer.Description = Description;
ProductGridContainer.Description.displayName = NAMES.Description;

ProductGridContainer.PartNumbers = PartNumbers;
ProductGridContainer.PartNumbers.displayName = NAMES.PartNumbers;

ProductGridContainer.Price = Price;
ProductGridContainer.Price.displayName = NAMES.Price;

ProductGridContainer.Availability = Availability;
ProductGridContainer.Availability.displayName = NAMES.Availability;

ProductGridContainer.Badges = Badges;
ProductGridContainer.Badges.displayName = NAMES.Badges;

ProductGridContainer.AddToCartActions = AddToCartActions;
ProductGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;

ProductGridContainer.AddToList = AddToList;
ProductGridContainer.AddToList.displayName = NAMES.AddToList;

export default ProductGridContainer;
